/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Divider,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BuildIcon from "@mui/icons-material/Build";
import FeedIcon from "@mui/icons-material/Feed";
import HandymanIcon from "@mui/icons-material/Handyman";
import BusinessIcon from "@mui/icons-material/Business";
import { Vocabulary } from "../../Utils/Vocabulary";
import { handleChangeAutocomplete, setStatuses } from "../../Utils/Utils";
import useCustomMemo from "../GenericComponents/useCustomMemo";
import { useContext, useEffect, useState } from "react";
import { OrderContext } from "../../Contexts/OrderContext";
import styles from "../../Styles/order.module.css";
import { styled } from "@mui/system";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/UrlEnum";
import { PartnerModel, TechnicianModel } from "../../Utils/Models";

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  color: "black",
  fontWeight: "bold",
  backgroundColor: "white",
}));

const GroupItems = styled("ul")({
  padding: 0,
});

export default function GeneralInformation() {
  const orderContext = useContext(OrderContext);
  const phoneStyle = useMediaQuery("(max-width: 800px)");
  const cache = useCustomMemo();
  const statuses =
    setStatuses(
      cache.statuses || [],
      cache.workflows || [],
      (orderContext.order.workflow && orderContext.order.workflow?._id) || null,
    ) || [];
  const users = cache.users || [];
  const locations = cache.locations || [];
  const [partners, setPartners] = useState<PartnerModel[]>([]);
  const [partnerTechnicians, setPartnerTechnicians] = useState<
    TechnicianModel[]
  >([]);

  // Fetch partners on mount
  useEffect(() => {
    getData(`${urlEnum.customers}?isPartner=true`).then((res: any) => {
      if (res.data.status === 200) {
        setPartners(res.data.result);
      }
    });
  }, []);

  // Fetch technicians when partner changes
  useEffect(() => {
    if (orderContext.order.partner?._id) {
      getData(
        `${urlEnum.technicians}/partner/${orderContext.order.partner._id}`,
      ).then((res: any) => {
        if (res.data.status === 200) {
          setPartnerTechnicians(res.data.result);
        }
      });
    } else {
      setPartnerTechnicians([]);
    }
  }, [orderContext.order.partner]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <BookmarkAddedIcon
              fontSize="small"
              style={{
                marginRight: 10,
                verticalAlign: "middle",
              }}
            />
            {Vocabulary.status}
          </Typography>
          {orderContext.order.workflow ? (
            <Autocomplete
              size="small"
              id="status"
              freeSolo={false}
              disablePortal
              options={statuses}
              value={orderContext.order.status || null}
              getOptionLabel={(option: any) => option.name}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(event: any, value: any) => {
                const newOrderContext = structuredClone(orderContext.order);
                newOrderContext.oldStatus = orderContext.order.status;
                if (value) newOrderContext.status = value;
                else newOrderContext.status = null;
                orderContext.setOrder(newOrderContext);
              }}
              renderInput={(params: any) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )}
              style={{ minWidth: 250 }}
            />
          ) : (
            <Typography style={{ minWidth: 250 }}>
              {Vocabulary.chooseWorkflowFirst}
            </Typography>
          )}
        </div>
        <Divider />
      </Grid>
      {/* Partner */}
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <BusinessIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.partner}
          </Typography>
          <Autocomplete
            size="small"
            id="partner"
            freeSolo={false}
            disablePortal
            options={partners}
            value={orderContext.order.partner || null}
            getOptionLabel={(option: any) =>
              `${option.companyName || option.name}`
            }
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event: any, value: any) => {
              const newOrder = handleChangeAutocomplete(
                event,
                value,
                orderContext.order,
                "partner",
              );
              // Clear partnerTechnician when partner changes
              newOrder.partnerTechnician = null;
              orderContext.setOrder(newOrder);
            }}
            renderInput={(params: any) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            style={{ minWidth: 250 }}
          />
        </div>
        <Divider />
      </Grid>
      {/* Partner Technician */}
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <HandymanIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.technician}
          </Typography>
          {orderContext.order.partner ? (
            <Autocomplete
              size="small"
              id="partnerTechnician"
              freeSolo={false}
              disablePortal
              options={partnerTechnicians}
              value={orderContext.order.partnerTechnician || null}
              getOptionLabel={(option: any) =>
                `${option.name}${option.phone ? ` - ${option.phone}` : ""}`
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(event: any, value: any) => {
                orderContext.setOrder(
                  handleChangeAutocomplete(
                    event,
                    value,
                    orderContext.order,
                    "partnerTechnician",
                  ),
                );
              }}
              noOptionsText={Vocabulary.noTechniciansForPartner}
              renderInput={(params: any) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              style={{ minWidth: 250 }}
            />
          ) : (
            <Typography style={{ minWidth: 250, color: "gray" }}>
              {Vocabulary.choosePartnerFirst}
            </Typography>
          )}
        </div>
        <Divider />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <EngineeringIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.handledBy}
          </Typography>
          <Autocomplete
            size="small"
            id="handledBy"
            freeSolo={false}
            disablePortal
            options={users}
            value={orderContext.order.handledBy || null}
            getOptionLabel={(option: any) => `${option.username}`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event: any, value: any) => {
              orderContext.setOrder(
                handleChangeAutocomplete(
                  event,
                  value,
                  orderContext.order,
                  "handledBy",
                ),
              );
            }}
            renderInput={(params: any) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            style={{ minWidth: 250 }}
          />
        </div>
        <Divider />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <BuildIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.serviceLocation}
          </Typography>
          <Autocomplete
            size="small"
            id="location"
            freeSolo={false}
            disablePortal
            options={locations}
            value={orderContext.order.location || null}
            getOptionLabel={(option: any) => option.name}
            isOptionEqualToValue={(option: any, value: any) =>
              option._id === value._id
            }
            onChange={(event: any, value: any) => {
              orderContext.setOrder(
                handleChangeAutocomplete(
                  event,
                  value,
                  orderContext.order,
                  "location",
                ),
              );
            }}
            renderInput={(params: any) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            style={{ minWidth: 250 }}
          />
        </div>
        <Divider />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <LocationOnIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.internalLocation}
          </Typography>
        </div>
        <Divider />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography>
            <FeedIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.tags}
          </Typography>
        </div>
        <Divider />
      </Grid>
    </Grid>
  );
}
